export function parseBoutiqueLocationLine(line) {
  if (!line || typeof line !== 'string') return null;
  const match = line.trim().match(/^(\d+)\s+(.+)$/);
  if (!match) return null;
  return { id: Number(match[1]), label: match[2].trim() };
}

function getFieldValue(fields, key) {
  if (!Array.isArray(fields)) return null;
  const field = fields.find((f) => f && f.key === key);
  return field?.value ?? null;
}

function parseCountryCodes(rawValue) {
  if (!rawValue) return [];
  if (Array.isArray(rawValue)) {
    return rawValue.map((c) => String(c).trim().toUpperCase()).filter(Boolean);
  }
  if (typeof rawValue === 'string') {
    const trimmed = rawValue.trim();
    if (!trimmed) return [];
    try {
      const parsed = JSON.parse(trimmed);
      if (Array.isArray(parsed)) {
        return parsed.map((c) => String(c).trim().toUpperCase()).filter(Boolean);
      }
    } catch (_) {
      /* fall through */
    }
    return trimmed.split(/[\n,|]/).map((c) => c.trim().toUpperCase()).filter(Boolean);
  }
  return [];
}

function parseBoutiqueLocationsField(rawValue) {
  if (!rawValue) return [];
  if (Array.isArray(rawValue)) return rawValue;
  if (typeof rawValue === 'string') {
    try {
      const parsed = JSON.parse(rawValue);
      if (Array.isArray(parsed)) return parsed;
    } catch (_) {
      /* fall through */
    }
    return rawValue.split('\n').map((line) => line.trim()).filter(Boolean);
  }
  return [];
}

let cachedAllowedIds = null;
let cachePromise = null;

export async function fetchAllowedBoutiqueIds() {
  const config = window.theme?.clickAndCollect;
  if (!config?.enabled) return [];

  if (cachedAllowedIds) return cachedAllowedIds;
  if (cachePromise) return cachePromise;

  cachePromise = (async () => {
    const accessToken = window.themeSettings?.storefront_access_token;
    if (!accessToken) return [];

    const type = config.regionMetaobjectType || 'region';
    const fieldKey = config.boutiqueLocationsField || 'boutique_locations';
    const countryCode = (window.store_country_code || 'US').toUpperCase();

    const query = `
      query GetRegionMetaobjects($type: String!) {
        metaobjects(type: $type, first: 250) {
          edges {
            node {
              fields { key value }
            }
          }
        }
      }
    `;

    const response = await fetch('/api/2023-07/graphql.json', {
      method: 'POST',
      headers: {
        'Content-Type': 'application/json',
        'X-Shopify-Storefront-Access-Token': accessToken,
      },
      body: JSON.stringify({ query, variables: { type } }),
    });

    if (!response.ok) return [];

    const data = await response.json();
    const edges = data?.data?.metaobjects?.edges || [];
    const nodes = edges.map((e) => e?.node).filter(Boolean);

    let lines = [];
    for (const node of nodes) {
      const fields = node.fields || [];
      const codes = parseCountryCodes(getFieldValue(fields, 'country_codes'));
      if (codes.length && !codes.includes(countryCode)) continue;

      const rawLocations = getFieldValue(fields, fieldKey);
      lines = parseBoutiqueLocationsField(rawLocations);
      if (lines.length) break;
    }

    const ids = lines
      .map(parseBoutiqueLocationLine)
      .filter(Boolean)
      .map((entry) => entry.id);

    cachedAllowedIds = ids;
    return ids;
  })();

  try {
    return await cachePromise;
  } finally {
    cachePromise = null;
  }
}

export function clearAllowedBoutiqueIdsCache() {
  cachedAllowedIds = null;
  cachePromise = null;
}
